import { Box, Typography, Divider, Popover, IconButton } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import BigNumber from "bignumber.js";
import { formatToPoundsOunces } from "../../../formatting/weightFormat";
import { removeHtmlFromString } from "../../../utilities/removeHtmlFromString";
import { useState } from "react";

interface DirectiveLog {
  log_type: "directive";
  top: string[];
  output: string[];
}

interface AmazonPricingLog {
  log_type: "ap";
  title: string;
  weight: number;
  componentQty: number;
  mediaTypes: string;
  margin: number;
  profit: string;
  proceeds: string;
  roi: number;
  fees: {
    cogs: number;
    inboundFee: number;
    storageFee: number;
    fbaFee: number;
    referralFee: number;
    variableFee: number;
    outboundFee: number;
    yomHandlingFee: number;
    yomHandlingFeeAdjustment: number;
  };
  marginZero: number;
  marginFifteen: number;
  marginThirty: number;
  totalCost: number;
  price: number;
}

type LogEntry = DirectiveLog | AmazonPricingLog;

interface PricetoolLogProps {
  content: LogEntry[];
  clearedTimestamp?: string;
}

function FeeInfoPopover({
  fees,
  price,
}: {
  fees: AmazonPricingLog["fees"];
  price: number;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const cogs = new BigNumber(fees.cogs);
  const outboundFee = new BigNumber(fees.outboundFee);
  const inboundFee = new BigNumber(fees.inboundFee);
  const storageFee = new BigNumber(fees.storageFee);
  const yomHandlingFee = new BigNumber(fees.yomHandlingFee);
  const yomHandlingFeeAdjustment = new BigNumber(fees.yomHandlingFeeAdjustment);
  const fbaFee = new BigNumber(fees.fbaFee);
  const referralFee = new BigNumber(fees.referralFee);
  const variableFee = new BigNumber(fees.variableFee);

  const yomFees = yomHandlingFee
    .plus(outboundFee)
    .plus(yomHandlingFeeAdjustment);
  const amzConstantFees = fbaFee
    .plus(inboundFee)
    .plus(storageFee)
    .plus(variableFee);
  const totalConstantFees = yomFees.plus(amzConstantFees).plus(cogs);
  const totalCost = totalConstantFees.plus(referralFee);

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        sx={{ color: "warning.main", padding: 0, ml: 0.5 }}
      >
        <InfoIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ fontStyle: "italic", mb: 1 }}>
            Cost Breakdown
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="body2">
                <strong>COGS:</strong>
              </Typography>
              <Box sx={{ height: 8 }} />
              <Typography variant="body2">
                <strong>YOM fees:</strong>
              </Typography>
              <Box sx={{ pl: 2.5 }}>
                <Typography variant="body2">YOM handling fee:</Typography>
                <Typography variant="body2">Adjusted handling fee:</Typography>
                <Typography variant="body2">Outbound shipping fee:</Typography>
              </Box>
              <Box sx={{ height: 8 }} />
              <Typography variant="body2">
                <strong>Amazon constant fees:</strong>
              </Typography>
              <Box sx={{ pl: 2.5 }}>
                <Typography variant="body2">Inbound fee:</Typography>
                <Typography variant="body2">Monthly storage fee:</Typography>
                <Typography variant="body2">Fulfillment fee:</Typography>
                <Typography variant="body2">Variable fee:</Typography>
              </Box>
              <Box sx={{ height: 8 }} />
              <Typography variant="body2">
                <strong>Total constant fees:</strong>
              </Typography>
              <Box sx={{ height: 8 }} />
              <Typography variant="body2">
                <strong>Amazon 15% referral fee:</strong>
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                ({price ? `$${price}` : "no"} listing price)
              </Typography>
              <Box sx={{ height: 8 }} />
              <Typography variant="body2">
                <strong>Total costs and fees:</strong>
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="body2">${cogs.toFixed(2)}</Typography>
              <Box sx={{ height: 8 }} />
              <Typography variant="body2">${yomFees.toFixed(2)}</Typography>
              <Box sx={{ pl: 2.5 }}>
                <Typography variant="body2">
                  ${yomHandlingFee.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  {yomHandlingFeeAdjustment.isEqualTo(0)
                    ? "---"
                    : `$${yomHandlingFeeAdjustment.toFixed(2)}`}
                </Typography>
                <Typography variant="body2">
                  ${outboundFee.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ height: 8 }} />
              <Typography variant="body2">
                ${amzConstantFees.toFixed(2)}
              </Typography>
              <Box sx={{ pl: 2.5 }}>
                <Typography variant="body2">
                  ${inboundFee.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  ${storageFee.toFixed(2)}
                </Typography>
                <Typography variant="body2">${fbaFee.toFixed(2)}</Typography>
                <Typography variant="body2">
                  ${variableFee.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ height: 8 }} />
              <Typography variant="body2">
                ${totalConstantFees.toFixed(2)}
              </Typography>
              <Box sx={{ height: 8 }} />
              <Typography variant="body2">
                {referralFee.isEqualTo(0)
                  ? "---"
                  : `$${referralFee.toFixed(2)}`}
              </Typography>
              <Box sx={{ height: 8 }} />
              <Box sx={{ height: 8 }} />
              <Typography variant="body2">${totalCost.toFixed(2)}</Typography>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

function DirectiveLogEntry({ log }: { log: DirectiveLog }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ color: "white" }}>
        Directive
      </Typography>
      <Box sx={{ mt: 1 }}>
        {log.top.map((line, idx) => (
          <Typography key={idx} variant="body2" sx={{ color: "white" }}>
            {line}
          </Typography>
        ))}
      </Box>
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{ color: "white", whiteSpace: "pre-wrap" }}
        >
          {log.output.join("\n")}
        </Typography>
      </Box>
    </Box>
  );
}

function AmazonPricingLogEntry({ log }: { log: AmazonPricingLog }) {
  const formatCurrencyFromDollars = (
    data: number | null | undefined
  ): string => {
    if (data === undefined || data === null) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(data);
  };

  const formatPercent = (data: number | null | string): string | null => {
    if (data !== null && data !== "") {
      const numberData = parseFloat(data.toString());
      if (!isNaN(numberData)) {
        return `${numberData.toFixed(1)}%`;
      }
    }
    return null;
  };

  const formatMarginTextRow = (
    percent: number,
    marginPrice: number
  ): string => {
    const usdFormatMargin = formatCurrencyFromDollars(marginPrice);
    const marginProfit = new BigNumber(marginPrice * percent).toFixed(2);
    const usdFormatProfit = formatCurrencyFromDollars(Number(marginProfit));
    return `${percent * 100}% | ${usdFormatMargin} | ${usdFormatProfit}`;
  };

  const formatSumAmazonFees = (fees: AmazonPricingLog["fees"]): string => {
    const inboundFee = new BigNumber(fees.inboundFee);
    const storageFee = new BigNumber(fees.storageFee);
    const fbaFee = new BigNumber(fees.fbaFee);
    const referralFee = new BigNumber(fees.referralFee);
    const variableFee = new BigNumber(fees.variableFee);

    return `$${inboundFee
      .plus(storageFee)
      .plus(fbaFee)
      .plus(referralFee)
      .plus(variableFee)
      .toFixed(2)}`;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ color: "white" }}>
        Amazon Pricing Log
      </Typography>
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{ color: "white", fontWeight: "bold", mt: 0.25 }}
        >
          Title: {removeHtmlFromString(log.title)}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "white", fontWeight: "bold", mt: 0.25 }}
        >
          {`${log.componentQty} ${log.mediaTypes} ${formatToPoundsOunces(
            log.weight
          )}`}
        </Typography>
        <Box sx={{ height: 8 }} />
        <Typography
          variant="body2"
          sx={{ color: "white", fontWeight: "bold", mt: 0.25 }}
        >
          Listing price:{" "}
          {log.price ? formatCurrencyFromDollars(log.price) : "---"}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "white", fontWeight: "bold", mt: 0.25 }}
        >
          COGS: {formatCurrencyFromDollars(log.fees.cogs)}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "white", fontWeight: "bold", mt: 0.25 }}
        >
          Estimated amz fees: {formatSumAmazonFees(log.fees)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", mt: 0.25 }}>
          <Typography
            variant="body2"
            sx={{ color: "white", fontWeight: "bold" }}
          >
            Total cost and fees: ${log.totalCost}
          </Typography>
          <FeeInfoPopover fees={log.fees} price={log.price} />
        </Box>
        <Typography
          variant="body2"
          sx={{ color: "white", fontWeight: "bold", mt: 0.25 }}
        >
          Margin: {formatPercent(log.margin) || "---"}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "white", fontWeight: "bold", mt: 0.25 }}
        >
          Profit: {log.profit || "---"}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "white", fontWeight: "bold", mt: 0.25 }}
        >
          Proceeds: {log.proceeds || "---"}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "white", fontWeight: "bold", mt: 0.25 }}
        >
          ROI: {formatPercent(log.roi) || "---"}
        </Typography>
      </Box>
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{ color: "white", fontFamily: "monospace", whiteSpace: "pre" }}
        >
          % | Price | Profit
        </Typography>
        <Typography variant="body2" sx={{ color: "white" }}>
          {formatMarginTextRow(0, log.marginZero)}
        </Typography>
        <Typography variant="body2" sx={{ color: "white" }}>
          {formatMarginTextRow(0.15, log.marginFifteen)}
        </Typography>
        <Typography variant="body2" sx={{ color: "white" }}>
          {formatMarginTextRow(0.3, log.marginThirty)}
        </Typography>
      </Box>
    </Box>
  );
}

export function PricetoolLog({ content, clearedTimestamp }: PricetoolLogProps) {
  return (
    <Box sx={{ p: "7px 14px" }}>
      {clearedTimestamp && (
        <Typography variant="body2" sx={{ color: "white", mb: 2 }}>
          Last Cleared: {clearedTimestamp}
        </Typography>
      )}
      {content.map((rec, i) => (
        <Box key={i}>
          {i > 0 && (
            <Divider sx={{ my: 2, borderColor: "rgba(255, 255, 255, 0.12)" }} />
          )}
          {rec.log_type === "ap" ? (
            <AmazonPricingLogEntry log={rec} />
          ) : (
            <DirectiveLogEntry log={rec} />
          )}
        </Box>
      ))}
    </Box>
  );
}
